import { useI18n } from '@ecom/i18n';
import { Button, copyText } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { env } from '@/lib/env';

/**
 * The « fiche de remise »: what the platform administrator tells the merchant when handing over a provisional password (lot P4, M2).
 *
 * It carries the address of the merchant space, the account's identifier, when the provisional password stops working and the three
 * steps the merchant follows - and NEVER the password itself. The password is shown once, by `TemporaryPasswordPanel`, and travels by
 * a different channel (in person, or any channel the administrator trusts); the sheet can therefore be copied and sent freely. The
 * address is a build setting (`VITE_MERCHANT_DASHBOARD_URL`): when it is missing the sheet says so instead of guessing a host.
 */
export function HandOverSheet({
  email,
  expiresAt,
}: {
  readonly email: string | null;
  readonly expiresAt: string;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const address = env.merchantDashboardUrl;
  const expires = new Date(expiresAt).toLocaleString();

  const steps = [t('admin.handover.step1'), t('admin.handover.step2'), t('admin.handover.step3')];

  function instructions(): string {
    return [
      t('admin.handover.title'),
      '',
      `${t('admin.handover.dashboardLabel')} : ${address ?? t('admin.handover.dashboardMissing')}`,
      ...(email === null ? [] : [`${t('admin.handover.accountLabel')} : ${email}`]),
      `${t('admin.handover.expiresLabel')} : ${expires}`,
      '',
      t('admin.handover.stepsTitle'),
      ...steps.map((step, index) => `${String(index + 1)}. ${step}`),
      '',
      t('admin.handover.noPassword'),
    ].join('\n');
  }

  async function copy(): Promise<void> {
    const ok = await copyText(instructions());
    setCopied(ok);
    if (ok) toast.success(t('admin.handover.copied'));
    else toast.error(t('admin.handover.copyFailed'));
  }

  return (
    <section
      aria-label={t('admin.handover.title')}
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface2 p-4"
    >
      <h3 className="font-display text-base font-semibold text-ink">{t('admin.handover.title')}</h3>
      <p className="text-sm text-inkFaint">{t('admin.handover.intro')}</p>

      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="text-inkFaint">{t('admin.handover.dashboardLabel')}</dt>
        <dd className="break-all text-ink" dir="ltr">
          {address ?? <span className="text-inkFaint">{t('admin.handover.dashboardMissing')}</span>}
        </dd>
        {email === null ? null : (
          <>
            <dt className="text-inkFaint">{t('admin.handover.accountLabel')}</dt>
            <dd className="break-all text-ink" dir="ltr">
              {email}
            </dd>
          </>
        )}
        <dt className="text-inkFaint">{t('admin.handover.expiresLabel')}</dt>
        <dd className="text-ink">{expires}</dd>
      </dl>

      <div>
        <p className="text-sm font-medium text-ink">{t('admin.handover.stepsTitle')}</p>
        <ol className="mt-1 list-decimal ps-5 text-sm text-inkSoft">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void copy();
          }}
        >
          {t('admin.handover.copy')}
        </Button>
        <span className="text-xs text-inkFaint" aria-live="polite">
          {copied ? t('admin.handover.copied') : t('admin.handover.noPassword')}
        </span>
      </div>
    </section>
  );
}
